import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";

import { useDashboard, Widget } from "@/modules";
import { ContentScheduleItem, getContentSchedule } from "@/modules/widgets/api/contentSchedule";
import { WidgetHeader } from "../WidgetHeader";
import { TremorTable } from "../table/TremorTable";
import { ITableData, IColumnDef } from "../table/types";
import { useWidgetFiltersController } from "../WidgetFilters/useWidgetFiltersController";
import { LoadingSpinnerIcon } from "@/components/icons/LoadingSpinnerIcon";
import { NoDataView } from "../NoDataView";

type ContentScheduleWidgetProps = {
    widget: Widget;
    showDeleteButton?: boolean;
    onWidgetDelete?: () => void;
    onMinimizeChange?: (isMinimized: boolean) => void;
    colSpan?: number;
};

const COLUMNS: IColumnDef[] = [
    { header: "Deadline", field: "deadline", metric: "deadline", metricType: "date", sortable: true },
    { header: "Type", field: "type", metric: "type", metricType: "text", sortable: true },
    { header: "Topic", field: "topic", metric: "topic", metricType: "text", sortable: false },
    { header: "Target Keyword", field: "targetKeyword", metric: "targetKeyword", metricType: "text", sortable: false },
];

function toTableData(items: ContentScheduleItem[]): ITableData {
    return {
        columns: COLUMNS,
        rows: items.map((item) => ({
            deadline: item.deadline,
            type: item.type,
            topic: item.topic,
            targetKeyword: item.targetKeyword,
        })),
        metadata: {
            hideTotalRow: true,
        },
    };
}

export const ContentScheduleWidget: React.FC<ContentScheduleWidgetProps> = ({
    widget,
    showDeleteButton = false,
    onWidgetDelete,
    onMinimizeChange,
    colSpan,
}) => {
    const { state: { selectedDealer } } = useDashboard();
    const [items, setItems] = useState<ContentScheduleItem[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const widgetFiltersController = useWidgetFiltersController();

    useEffect(() => {
        const clientId = selectedDealer?.dealerRefId;
        if (!clientId) {
            setItems([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        getContentSchedule(clientId)
            .then((data) => {
                if (!cancelled) setItems(data);
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedDealer?.dealerRefId]);

    const tableData = useMemo(() => (items && items.length > 0 ? toTableData(items) : null), [items]);

    const handleMinimizeToggle = () => {
        const next = !isMinimized;
        setIsMinimized(next);
        onMinimizeChange?.(next);
    };

    const handleColumnReorder = (columns: IColumnDef[]) => void columns;

    if (!loading && items !== null && items.length === 0) return null;

    return (
        <div
            className={classNames(
                "bg-white rounded-[16px] flex-1 flex flex-col transition-all duration-300 border border-[#c9d1d4]",
                isMinimized ? "h-fit" : "h-full min-h-[16rem]",
            )}
        >
            <WidgetHeader
                widget={widget}
                title={widget.title}
                iconType="table"
                showDeleteButton={showDeleteButton}
                onWidgetDelete={onWidgetDelete}
                isMinimized={isMinimized}
                onMinimizeToggle={handleMinimizeToggle}
                colSpan={colSpan}
                allowInsights={false}
                allowEdit={false}
                widgetFiltersController={widgetFiltersController}
                onColumnReorder={handleColumnReorder}
            />
            {!isMinimized && (
                <div className="relative transition-all font-roboto duration-300 ease-in-out min-h-32 h-full flex flex-col grow flex-1">
                    {loading && (
                        <div className="flex items-center justify-center flex-1 py-20 absolute h-full w-full z-100 top-0 left-0 bg-white/40">
                            <LoadingSpinnerIcon />
                        </div>
                    )}
                    {tableData ? (
                        <div className="h-auto w-full">
                            <TremorTable data={tableData} widget={widget} />
                        </div>
                    ) : (
                        !loading && <NoDataView />
                    )}
                </div>
            )}
        </div>
    );
};
